import Dexie, { type EntityTable } from "dexie";
import {
  hasOlderUnsentFor,
  isPrunableFailure,
  partitionByOwner,
  selectSentUnresolved,
} from "./pendingMutationRules.ts";

export type PendingMutationStatus = "queued" | "sending" | "awaiting" | "failed";

export interface PendingMutation {
  operationUuid: string;
  userUuid?: string;
  sent?: boolean;
  label?: string;
  endpoint: string;
  method: "POST" | "PUT" | "DELETE";
  body: unknown;
  status: PendingMutationStatus;
  retries: number;
  createdAt: number;
}

class PendingMutationsDexie extends Dexie {
  pendingMutations!: EntityTable<PendingMutation, "operationUuid">;

  constructor() {
    super("pico-offline-queue");
    this.version(1).stores({
      pendingMutations: "operationUuid, status, createdAt",
    });
  }
}

export const pendingMutationsDb = new PendingMutationsDexie();

const changeListeners = new Set<() => void>();

export function subscribeToPendingMutations(listener: () => void): () => void {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

function notifyChanged(): void {
  changeListeners.forEach((listener) => listener());
}

export async function queueMutation(
  entry: Omit<PendingMutation, "status" | "retries" | "createdAt">,
): Promise<number> {
  const createdAt = Date.now();
  await pendingMutationsDb.pendingMutations.put({
    ...entry,
    status: "queued",
    retries: 0,
    createdAt,
  });
  notifyChanged();
  return createdAt;
}

export async function listPendingMutations(): Promise<PendingMutation[]> {
  return pendingMutationsDb.pendingMutations.where("status").equals("queued").sortBy("createdAt");
}

export async function listAllMutations(userUuid: string): Promise<PendingMutation[]> {
  const all = await pendingMutationsDb.pendingMutations.orderBy("createdAt").reverse().toArray();
  return all.filter((entry) => entry.userUuid === userUuid);
}

export async function clearPendingMutations(): Promise<void> {
  await pendingMutationsDb.pendingMutations.clear();
  notifyChanged();
}

export async function discardMutationsOfOtherUsers(userUuid: string): Promise<void> {
  const { foreign, unattributed } = partitionByOwner(
    await pendingMutationsDb.pendingMutations.toArray(),
    userUuid,
  );
  if (foreign.length === 0 && unattributed.length === 0) return;
  await pendingMutationsDb.pendingMutations.bulkDelete(foreign.map((e) => e.operationUuid));
  await Promise.all(
    unattributed.map((entry) =>
      pendingMutationsDb.pendingMutations.update(entry.operationUuid, {
        status: "failed",
        userUuid,
      }),
    ),
  );
  notifyChanged();
}

export async function hasOlderUnsentMutation(
  entry: Pick<PendingMutation, "operationUuid" | "endpoint" | "createdAt">,
): Promise<boolean> {
  const unfinished = await pendingMutationsDb.pendingMutations
    .where("status")
    .anyOf("queued", "sending")
    .toArray();
  return hasOlderUnsentFor(unfinished, entry);
}

export async function listSentUnresolvedMutations(userUuid: string): Promise<PendingMutation[]> {
  const unresolved = await pendingMutationsDb.pendingMutations.orderBy("createdAt").toArray();
  return selectSentUnresolved(unresolved, userUuid);
}

// An interrupted send and an unconfirmed write both need sending again; the write is idempotent
// because the entry keeps its original operationUuid. The age is not judged here — that happens
// once, immediately before sending, so there is no second path that could skip it.
export async function requeueUnconfirmedMutations(): Promise<void> {
  const unconfirmed = await pendingMutationsDb.pendingMutations
    .where("status")
    .anyOf("sending", "awaiting")
    .toArray();
  if (unconfirmed.length === 0) return;
  await Promise.all(
    unconfirmed.map((entry) =>
      pendingMutationsDb.pendingMutations.update(entry.operationUuid, { status: "queued" }),
    ),
  );
  notifyChanged();
}

export async function discardExpiredFailures(): Promise<void> {
  const now = Date.now();
  const expired = await pendingMutationsDb.pendingMutations
    .filter((entry) => isPrunableFailure(entry, now))
    .toArray();
  if (expired.length === 0) return;
  await pendingMutationsDb.pendingMutations.bulkDelete(expired.map((e) => e.operationUuid));
  notifyChanged();
}

// Like markAwaiting: a terminal entry stays terminal, whichever write lands second.
export async function markRetryable(operationUuid: string, retries: number): Promise<void> {
  const updated = await pendingMutationsDb.pendingMutations
    .where("operationUuid")
    .equals(operationUuid)
    .and((entry) => entry.status !== "failed")
    .modify({ status: "queued", retries });
  if (updated > 0) notifyChanged();
}

export async function markSending(operationUuid: string): Promise<void> {
  const updated = await pendingMutationsDb.pendingMutations
    .where("operationUuid")
    .equals(operationUuid)
    .and((entry) => entry.status !== "failed")
    .modify({ status: "sending", sent: true });
  if (updated > 0) notifyChanged();
}

// The confirmation can land before the request promise settles; the terminal state wins.
export async function markAwaiting(operationUuid: string): Promise<void> {
  const updated = await pendingMutationsDb.pendingMutations
    .where("operationUuid")
    .equals(operationUuid)
    .and((entry) => entry.status !== "failed")
    .modify({ status: "awaiting", sent: true });
  if (updated > 0) notifyChanged();
}

export async function markFailed(operationUuid: string, retries: number): Promise<void> {
  await pendingMutationsDb.pendingMutations.update(operationUuid, { status: "failed", retries });
  notifyChanged();
}

export async function removeMutation(operationUuid: string): Promise<void> {
  await pendingMutationsDb.pendingMutations.delete(operationUuid);
  notifyChanged();
}
